// What a data widget shows before it has data: the real reason, and the
// quickest honest way to fix it. Never a placeholder number.

import { useState } from "react";
import { BriefcaseBusiness, Plug, PenLine, Sparkles, TriangleAlert, X } from "lucide-react";
import { openChat } from "../agent/store";
import { SPEC } from "../../shared/catalog";
import { fillAsk } from "../../shared/askWords";
import { firstStep, readsFor } from "../../shared/fits";
import { pushValue, typedStreamKey } from "../state/sources";
import { WidgetIcon } from "../chrome/icons";
import { updateItem } from "../state/actions";
import { Setup } from "./setup";
import { timeAgo } from "../../shared/format";
import { keyService } from "../../shared/keyServices";
import type { Stream, WidgetItem, WidgetType } from "../../shared/types";
import { api } from "../lib/api";
import { set, toast, useStore } from "../state/store";
import { jobOf, jobWhen, openJob, useJobRunning } from "./job";
import { SignInAgain, useSignedOut } from "./SignInAgain";
import { OursEmpty } from "./ours";
import { isPipelineStream } from "../../shared/pipeline";

function Illustration({ type }: { type: WidgetItem["type"] }) {
  if (type === "chart" || type === "metric")
    return (
      <svg className="empty-art" viewBox="0 0 120 40" preserveAspectRatio="none">
        <path d="M2 34 C 20 30, 26 22, 40 24 S 62 12, 76 16 S 100 6, 118 4" fill="none" strokeDasharray="3 4" />
      </svg>
    );
  if (type === "goal")
    return (
      <svg className="empty-art empty-art-ring" viewBox="0 0 40 40">
        <circle cx="20" cy="20" r="16" fill="none" strokeDasharray="3 4" />
      </svg>
    );
  return (
    <svg className="empty-art" viewBox="0 0 120 40" preserveAspectRatio="none">
      <line x1="2" y1="8" x2="90" y2="8" strokeDasharray="3 4" />
      <line x1="2" y1="20" x2="70" y2="20" strokeDasharray="3 4" />
      <line x1="2" y1="32" x2="104" y2="32" strokeDasharray="3 4" />
    </svg>
  );
}

const GIVES: Record<string, string> = { number: "one number", list: "a list", table: "a table", text: "text", json: "data" };
const SHOWS: Partial<Record<WidgetType, string>> = { map: "a number for each country", metric: "one number", goal: "one number", chart: "a number over time", list: "a list", table: "a table or a list", status: "a list of checks", brief: "text" };

/** Whether a widget was given data of a shape it cannot show: one number for a world map, a list for a number. */
export function misfit(item: WidgetItem, stream: Stream | null): boolean {
  const reads = SPEC[item.type]?.reads;
  if (!reads || !stream || stream.shape === "empty" || reads.includes("json") || item.type === "custom") return false;
  return !reads.includes(stream.shape);
}

/**
 * A widget connected to a source that gives what it cannot show: said plainly, with the way to one that fits. A
 * service with a read that does fit (a goal on Lemon Squeezy's order list, which can count the orders instead)
 * opens on that service's reads the widget can show, with nothing to paste.
 */
export function Misfit({ item, stream, readOnly: ro }: { item: WidgetItem; stream: Stream; readOnly: boolean }) {
  // on a board shared with you, where a widget's data comes from is the owner's to set (sharing)
  const readOnly = useStore((s) => ro || !!s.guest);
  const label = SPEC[item.type].label.toLowerCase();
  const service = useStore((s) => {
    const p = stream.pullId ? s.pulls[stream.pullId] : undefined;
    const svc = p?.config.service ? keyService(p.config.service) : null;
    return svc && readsFor(item, svc.id).length ? svc : null;
  });
  return (
    <div className="empty">
      <Illustration type={item.type} />
      <span className="empty-title">This source does not fit</span>
      <span className="empty-sub">
        It gives {GIVES[stream.shape] ?? "data"}, and a {label} shows {SHOWS[item.type] ?? "something else"}.
        {service ? ` ${service.name} has ${SHOWS[item.type] === "one number" ? "numbers" : "reads"} that fit.` : ""}
      </span>
      {!readOnly && (
        <div className="empty-actions">
          <button
            className="btn btn-sm btn-primary"
            onClick={() => set({ panel: "connect", panelArg: service ? { tab: "data", item: item.id, stream: null, keyService: service.id } : { tab: "data", stream: item.stream ?? null, item: item.id } })}
            data-interactive
          >
            <Plug size={13} /> {service ? `Pick one from ${service.name}` : "Connect one that fits"}
          </button>
        </div>
      )}
    </div>
  );
}

export function EmptyData({ item, stream, readOnly: ro }: { item: WidgetItem; stream: Stream | null; readOnly: boolean }) {
  // on a board shared with you, where a widget's data comes from is the owner's to set (sharing)
  const readOnly = useStore((s) => ro || !!s.guest);
  const [typing, setTyping] = useState(false);
  const [val, setVal] = useState("");
  const running = useJobRunning(item.id) > 0;
  const numeric = SPEC[item.type].reads?.includes("number");
  const openData = () => set({ panel: "connect", panelArg: { tab: "data", stream: item.stream ?? null, item: item.id } });
  // a source whose service's saved key is gone: the fix is saving the key again, not editing the source
  const lost = useStore((s) => {
    const p = stream?.pullId ? s.pulls[stream.pullId] : undefined;
    const svc = p?.config.service ? keyService(p.config.service) : null;
    return svc && !s.services[svc.id] ? svc : null;
  });
  // a source whose connection signed out: the fix is signing in again, here (a hook, so before any early return)
  const signedOut = useSignedOut(stream);
  // ContextBolt's own say what each view shows and connect through their product's page
  if (SPEC[item.type].viaConnect) return <OursEmpty item={item} stream={stream} readOnly={readOnly} />;
  // a client pipeline's own numbers come from its deals, never from a source: they wait for a deal with a value
  if (isPipelineStream(item.stream))
    return (
      <div className="empty">
        <Illustration type={item.type} />
        <span className="empty-title">{/^pipeline-won-/.test(item.stream!) ? "Nothing won yet" : "No deal values yet"}</span>
        <span className="empty-sub">Give a deal its value and this adds them up.</span>
      </div>
    );
  if (readOnly)
    return (
      <div className="empty">
        <Illustration type={item.type} />
        <span className="empty-title">No data yet</span>
      </div>
    );
  // a widget given a job: its job fills it, running now or at its time, and one that failed says why, never "fix the source"
  const job = jobOf(item);
  if (job && running)
    return (
      <div className="empty">
        <span className="spinner" />
        <span className="empty-title">Running its job</span>
        <span className="empty-sub">It fills in when your agent is done.</span>
      </div>
    );
  if (job)
    return (
      <div className={stream?.error ? "empty is-error" : "empty"}>
        {stream?.error ? <TriangleAlert size={16} /> : <Illustration type={item.type} />}
        <span className="empty-title">{stream?.error ? "Its job did not finish" : job.prompt ? "Its job fills this" : "Say what its job does"}</span>
        <span className="empty-sub" title={stream?.error ?? undefined}>
          {stream?.error ?? (job.prompt ? jobWhen(job) : "In its settings, in your own words")}
        </span>
        <div className="empty-actions">
          <button className="btn btn-sm" onClick={() => openJob(item.id)} data-interactive>
            <BriefcaseBusiness size={13} /> Open its job
          </button>
        </div>
      </div>
    );
  // a goal, a Number, a Chart, or a widget a service fills: its first step, as every widget's reads (setup.tsx)
  if (firstStep(item) && (!item.stream || !stream?.error) && !(item.props.awaiting === "push" && !stream?.updatedAt)) return <FirstStep item={item} />;
  if (!item.stream)
    return (
      <div className="empty">
        <Illustration type={item.type} />
        <span className="empty-title">Not connected yet</span>
        {/* what fills it, said in the moment (Features 4): a source, or the agent finding one */}
        <span className="empty-sub">It fills in from a source you connect, or your agent can find one.</span>
        <div className="empty-actions">
          <button className="btn btn-sm btn-primary" onClick={openData} data-interactive>
            <Plug size={13} /> Connect
          </button>
          <AskToFill item={item} />
        </div>
      </div>
    );
  // made by "Send it from your tools": the key exists, the first value has not come yet
  if (item.props.awaiting === "push" && !stream?.updatedAt)
    return (
      <div className="empty">
        <Illustration type={item.type} />
        <span className="empty-title">Waiting for the first value</span>
        <span className="empty-sub">It fills in when your tool sends it.</span>
        <div className="empty-actions">
          <button className="btn btn-sm" onClick={() => set({ panel: "connect", panelArg: { tab: "data", push: true, stream: item.stream, item: item.id } })} data-interactive>
            How to send it
          </button>
        </div>
      </div>
    );
  if (stream?.error)
    return (
      <div className="empty is-error">
        <TriangleAlert size={16} />
        <span className="empty-title">Could not read it</span>
        <span className="empty-sub" title={stream.error}>
          {stream.error}
        </span>
        <span className="empty-sub faint">Tried {timeAgo(stream.errorAt)}</span>
        <div className="empty-actions">
          {signedOut ? (
            <SignInAgain conn={signedOut} />
          ) : lost ? (
            <button className="btn btn-sm btn-primary" onClick={() => set({ panel: "connect", panelArg: { tab: "data", keyService: lost.id } })} data-interactive>
              <Plug size={13} /> Connect {lost.name}
            </button>
          ) : (
            <button className="btn btn-sm" onClick={() => set({ panel: "source", panelArg: { pullId: stream.pullId, stream: stream.key } })} data-interactive>
              Fix the source
            </button>
          )}
        </div>
      </div>
    );
  const submit = async () => {
    const n = Number(val.replace(/[,$£€%\s]/g, ""));
    if (!val.trim() || !Number.isFinite(n)) {
      toast({ level: "warn", title: "That is not a number" });
      return;
    }
    try {
      await api(`/api/streams/${encodeURIComponent(item.stream!)}`, { body: { value: n } });
      setTyping(false);
      setVal("");
    } catch (e) {
      toast({ level: "error", title: "Could not save it", body: (e as Error).message });
    }
  };
  return (
    <div className="empty">
      <Illustration type={item.type} />
      <span className="empty-title">Not connected yet</span>
      {!typing && <span className="empty-sub">It fills in from a source you connect{numeric ? ", a number you type" : ""}, or your agent can find one.</span>}
      {typing ? (
        <form
          className="empty-entry"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
          data-interactive
        >
          <input autoFocus inputMode="decimal" aria-label="The number" placeholder="Type a number" value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => e.key === "Escape" && setTyping(false)} />
          <button className="btn btn-sm btn-primary" type="submit">
            Save
          </button>
          <button
            className="icon-btn"
            type="button"
            aria-label="Cancel"
            title="Cancel"
            onClick={() => {
              setTyping(false);
              setVal("");
            }}
          >
            <X size={13} />
          </button>
        </form>
      ) : (
        <div className="empty-actions">
          <button className="btn btn-sm btn-primary" onClick={openData} data-interactive>
            <Plug size={13} /> Connect
          </button>
          {numeric && (
            <button className="btn btn-sm" onClick={() => setTyping(true)} data-interactive>
              <PenLine size={13} /> Type it
            </button>
          )}
          <AskToFill item={item} />
        </div>
      )}
    </div>
  );
}

/**
 * An empty widget's ask for the agent (Features 4): the words go in the chat's box, ready to send. They are the board's
 * own, from the widget's kind, with its title only as a name in quotes (Fixes 8): on a shared board a coworker writes
 * the title, and their words must never land in the owner's box as the owner's request.
 */
function AskToFill({ item }: { item: WidgetItem }) {
  return (
    <button className="btn btn-sm" onClick={() => openChat(fillAsk(SPEC[item.type]?.label.toLowerCase(), item.title), { send: false })} data-interactive data-tip="Put it in the chat, ready to send">
      <Sparkles size={13} /> Ask your agent
    </button>
  );
}

/**
 * A data widget's first step, in the look every widget's has (setup.tsx): what it shows once connected, which
 * service fills it and how, one button that opens the way in (that service's own form when one service fills it),
 * and, for a number, typing it in as the quiet other way.
 */
function FirstStep({ item }: { item: WidgetItem }) {
  const step = firstStep(item)!;
  const [typing, setTyping] = useState(false);
  const [val, setVal] = useState("");
  const numeric = SPEC[item.type].reads?.includes("number");
  const open = () => set({ panel: "connect", panelArg: { tab: "data", stream: item.stream ?? null, item: item.id, ...(step.service ? { keyService: step.service } : {}) } });
  const submit = async () => {
    const n = Number(val.replace(/[,$£€%\s]/g, ""));
    if (!val.trim() || !Number.isFinite(n)) {
      toast({ level: "warn", title: "That is not a number" });
      return;
    }
    // a widget with no stream yet gets one of its own, never one another stream, source or widget has
    const key = typedStreamKey(item);
    try {
      await pushValue(key, { value: n });
      if (!item.stream) updateItem(item.id, { stream: key }, "Type a value");
      setTyping(false);
      setVal("");
    } catch (e) {
      toast({ level: "error", title: "Could not save it", body: (e as Error).message });
    }
  };
  return (
    <Setup
      className="is-step"
      tile
      icon={<WidgetIcon type={item.type} />}
      title={step.title}
      sub={typing ? undefined : step.sub}
      note={
        numeric && !typing ? (
          <button className="link-btn" onClick={() => setTyping(true)} data-interactive>
            Or type it in
          </button>
        ) : undefined
      }
    >
      {typing ? (
        <form
          className="setup-form"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <input autoFocus inputMode="decimal" aria-label="The number" placeholder="Type a number" value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => e.key === "Escape" && setTyping(false)} />
          <button className="btn btn-sm btn-primary" type="submit">
            Save
          </button>
          <button
            className="icon-btn"
            type="button"
            aria-label="Cancel"
            title="Cancel"
            onClick={() => {
              setTyping(false);
              setVal("");
            }}
          >
            <X size={13} />
          </button>
        </form>
      ) : (
        <button className="btn btn-sm btn-primary" onClick={open}>
          <Plug size={13} /> {step.button}
        </button>
      )}
    </Setup>
  );
}
